import {
  composeFullName,
  isValidLinkedInProfileUrl,
  normalizeLinkedInUrl,
} from '@shared/constants/profile-name';

import type {
  IdentityEditScope,
  ProfileIdentityDraft,
  ReferenceNames,
} from './profile-identity-draft.model';

/** Field-level validation errors, keyed by draft field name. Empty = valid. */
export type ValidationErrors = Partial<Record<keyof ProfileIdentityDraft, string>>;

/** Inline message when the backend rejects the URL as already used (409). */
export const LINKEDIN_URL_TAKEN_MESSAGE = 'Another profile already uses this LinkedIn URL.';

export const NAME_REQUIRED_MESSAGE = 'Enter at least a first name or a last name.';
export const LINKEDIN_URL_INVALID_MESSAGE =
  'Enter a LinkedIn member profile URL (https://www.linkedin.com/in/…).';

/**
 * Pure business validation for an identity draft. The rules mirror the backend's
 * `validateIdentityEdit` exactly; both call the same
 * `@shared/constants/profile-name` helpers, so a URL the form accepts cannot be
 * rejected by the API.
 *
 * Only ONE of the two names is required: mononyms exist, and the collectors
 * routinely put the whole name in `first_name` and leave `last_name` empty.
 * Requiring both would make those profiles uneditable.
 */
export function validateDraft(
  draft: ProfileIdentityDraft,
  scope: IdentityEditScope = 'global',
): ValidationErrors {
  const errors: ValidationErrors = {};
  // A licence's spelling (#703) has no rule to break: a blank field falls back
  // to the reference name, and the LinkedIn URL is not part of it.
  if (scope === 'licence') return errors;

  if (!draft.firstName.trim() && !draft.lastName.trim()) {
    errors.firstName = NAME_REQUIRED_MESSAGE;
  }

  const url = draft.linkedinUrl.trim();
  if (!url || !isValidLinkedInProfileUrl(normalizeLinkedInUrl(url))) {
    errors.linkedinUrl = LINKEDIN_URL_INVALID_MESSAGE;
  }

  return errors;
}

/**
 * The full name the save will produce, for the dialog's read-only preview. Same
 * composition the backend applies, so what the admin reads is what gets stored.
 */
export function previewFullName(
  draft: ProfileIdentityDraft,
  scope: IdentityEditScope = 'global',
  reference: ReferenceNames = { firstName: '', lastName: '' },
): string {
  // For a licence a blank field shows the reference name, so the preview does too.
  const fallback = (typed: string, ref: string) =>
    scope === 'licence' ? typed.trim() || ref : typed.trim();
  return composeFullName(
    fallback(draft.firstName, reference.firstName),
    fallback(draft.lastName, reference.lastName),
  );
}
